<template>
  <div class="ad-platform-page">
    <div class="page-heading">
      <div>
        <h1>投放媒体应用</h1>
        <p>维护广告展示来源应用；拉黑后，该包名发起的新签约下单将被拒绝。</p>
      </div>
      <a-button :loading="loading" @click="fetchList">
        <template #icon><ReloadOutlined /></template>
        刷新
      </a-button>
    </div>

    <a-row :gutter="[12, 12]" class="summary-grid">
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card size="small" :bordered="false" class="summary-card">
          <a-statistic title="应用数" :value="summary.appCount" />
        </a-card>
      </a-col>
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card size="small" :bordered="false" class="summary-card">
          <a-statistic title="发起签约" :value="summary.signRequestCount" />
        </a-card>
      </a-col>
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card size="small" :bordered="false" class="summary-card">
          <a-statistic title="签约成功" :value="summary.signSuccessCount" />
        </a-card>
      </a-col>
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card size="small" :bordered="false" class="summary-card">
          <a-statistic title="退款数" :value="summary.refundOrderCount" />
        </a-card>
      </a-col>
    </a-row>

    <a-card :bordered="false" class="table-card">
      <div class="toolbar" role="search" aria-label="投放媒体应用筛选">
        <a-input-search
          v-model:value="query.keyword"
          allow-clear
          aria-label="搜索包名或应用名称"
          placeholder="搜索包名或应用名称"
          class="keyword-input"
          @search="handleSearch"
        />
        <a-select
          v-model:value="query.isBlacklisted"
          allow-clear
          aria-label="拉黑状态"
          placeholder="全部状态"
          class="filter-select"
          @change="handleFilterChange"
        >
          <a-select-option :value="true">已拉黑</a-select-option>
          <a-select-option :value="false">正常</a-select-option>
        </a-select>
        <a-select
          v-model:value="query.nameStatus"
          allow-clear
          aria-label="应用名称完善状态"
          placeholder="名称状态"
          class="filter-select"
          @change="handleFilterChange"
        >
          <a-select-option value="unnamed">待补名称</a-select-option>
          <a-select-option value="named">已补名称</a-select-option>
        </a-select>
        <a-button @click="resetQuery">重置</a-button>
      </div>

      <a-alert
        type="info"
        show-icon
        class="source-tip"
        message="包名来自签约请求头 X-Requested-With；未传该请求头时保持放行。统计字段由独立任务写入，本页只读展示。"
      />

      <a-table
        :columns="columns"
        :data-source="list"
        :loading="loading"
        :pagination="pagination"
        :scroll="{ x: 1770 }"
        :row-class-name="rowClassName"
        row-key="id"
        size="middle"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'id'">
            <span class="number-cell">{{ record.id ?? "—" }}</span>
          </template>

          <template v-else-if="column.dataIndex === 'appName'">
            <span v-if="record.appName" class="app-name">{{ record.appName }}</span>
            <a-tag v-else color="warning">待补充</a-tag>
          </template>

          <template v-else-if="column.dataIndex === 'packageName'">
            <a-typography-text :copyable="{ text: record.packageName }" class="package-name">
              {{ record.packageName }}
            </a-typography-text>
          </template>

          <template v-else-if="column.dataIndex === 'url'">
            <a-typography-text
              v-if="record.url"
              :copyable="{ text: record.url, onCopy: handleUrlCopy }"
              class="url-cell"
            >
              <a-tooltip :title="record.url">
                <span class="ellipsis-cell">{{ record.url }}</span>
              </a-tooltip>
            </a-typography-text>
          </template>

          <template v-else-if="column.dataIndex === 'isBlacklisted'">
            <a-tag v-if="record.isBlacklisted" color="error">
              <template #icon><StopOutlined /></template>
              已拉黑
            </a-tag>
            <a-tag v-else color="success">
              <template #icon><CheckCircleOutlined /></template>
              正常
            </a-tag>
          </template>

          <template v-else-if="column.dataIndex === 'signSuccessRate'">
            <span class="number-cell">{{ formatRate(record.signSuccessRate) }}</span>
          </template>

          <template v-else-if="column.dataIndex === 'refundRate'">
            <span class="number-cell">{{ formatRate(record.refundRate) }}</span>
          </template>

          <template v-else-if="numberFields.includes(column.dataIndex)">
            <span class="number-cell">{{ formatCount(record[column.dataIndex]) }}</span>
          </template>

          <template v-else-if="column.dataIndex === 'statsUpdatedAt'">
            <span :class="{ 'muted-text': !record.statsUpdatedAt }">
              {{ record.statsUpdatedAt || "尚未统计" }}
            </span>
          </template>

          <template v-else-if="column.dataIndex === 'action'">
            <a-button type="link" class="edit-button" @click="openEdit(record)">
              <template #icon><EditOutlined /></template>
              编辑
            </a-button>
          </template>
        </template>

        <template #emptyText>
          <a-empty description="暂无符合条件的投放应用" />
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      title="编辑投放应用"
      ok-text="保存"
      cancel-text="取消"
      :confirm-loading="saving"
      :ok-button-props="{ danger: form.isBlacklisted }"
      @ok="handleSave"
    >
      <a-alert
        v-if="form.isBlacklisted"
        type="warning"
        show-icon
        class="blacklist-warning"
        message="保存后，该包名的新签约下单会立即被拒绝；已有签约不受影响。"
      />
      <a-form layout="vertical">
        <a-form-item label="来源包名">
          <a-input :value="form.packageName" disabled />
        </a-form-item>
        <a-form-item label="应用名称">
          <a-input
            v-model:value="form.appName"
            :maxlength="128"
            show-count
            allow-clear
            placeholder="例如：Instagram"
          />
          <div class="field-help">填写运营可识别的名称；包名仍作为唯一匹配依据。</div>
        </a-form-item>
        <a-form-item label="拉黑状态">
          <a-switch
            v-model:checked="form.isBlacklisted"
            checked-children="已拉黑"
            un-checked-children="正常"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import {
  CheckCircleOutlined,
  EditOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { analyticsUrl } from "@/utils/api";
import { get, postJson } from "@/utils/request";

const API_PATH = "/operate/ad-platform-app";

const query = reactive({
  keyword: "",
  isBlacklisted: undefined,
  nameStatus: undefined,
  sortField: "",
  sortOrder: "",
});

const loading = ref(false);
const saving = ref(false);
const modalVisible = ref(false);
const list = ref([]);
const summary = reactive({
  appCount: 0,
  signRequestCount: 0,
  signSuccessCount: 0,
  payOrderCount: 0,
  refundOrderCount: 0,
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const form = reactive({
  id: undefined,
  packageName: "",
  appName: "",
  isBlacklisted: false,
});

const numberFields = [
  "signRequestCount",
  "signSuccessCount",
  "payOrderCount",
  "refundOrderCount",
];

const columns = [
  {
    title: "应用ID",
    dataIndex: "id",
    width: 90,
    fixed: "left",
    align: "right",
    sorter: true,
  },
  { title: "应用名称", dataIndex: "appName", width: 160, fixed: "left", sorter: true },
  { title: "来源包名", dataIndex: "packageName", width: 250, fixed: "left", sorter: true },
  { title: "详情页链接", dataIndex: "url", width: 260 },
  { title: "状态", dataIndex: "isBlacklisted", width: 100 },
  { title: "发起签约", dataIndex: "signRequestCount", width: 110, align: "right", sorter: true },
  { title: "签约成功", dataIndex: "signSuccessCount", width: 110, align: "right", sorter: true },
  { title: "签约成功率", dataIndex: "signSuccessRate", width: 120, align: "right", sorter: true },
  { title: "支付成功", dataIndex: "payOrderCount", width: 110, align: "right", sorter: true },
  { title: "退款数", dataIndex: "refundOrderCount", width: 100, align: "right", sorter: true },
  { title: "退款率", dataIndex: "refundRate", width: 100, align: "right", sorter: true },
  { title: "统计更新时间", dataIndex: "statsUpdatedAt", width: 170, sorter: true },
  { title: "操作", dataIndex: "action", width: 90, fixed: "right" },
];

const formatCount = (value) => Number(value || 0).toLocaleString("zh-CN");

const formatRate = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0.00%";
  return `${(number * 100).toFixed(2)}%`;
};

const applySummary = (data = {}) => {
  Object.keys(summary).forEach((key) => {
    summary[key] = Number(data[key] || 0);
  });
};

const fetchList = async () => {
  loading.value = true;
  try {
    const json = await get(analyticsUrl(`${API_PATH}/list`), {
      pageIdx: pagination.current,
      pageSize: pagination.pageSize,
      keyword: query.keyword,
      isBlacklisted: query.isBlacklisted,
      nameStatus: query.nameStatus,
      sortField: query.sortField,
      sortOrder: query.sortOrder,
    });
    if (json.code !== 200) {
      message.error(json.content || "查询失败");
      return;
    }
    list.value = json.data?.data || [];
    pagination.total = json.data?.dataSum || 0;
    pagination.current = json.data?.pageIdx || pagination.current;
    pagination.pageSize = json.data?.pageSize || pagination.pageSize;
    applySummary(json.data?.summary);
  } catch {
    message.error("查询失败，请检查网络后重试");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  pagination.current = 1;
  fetchList();
};

const handleFilterChange = () => {
  pagination.current = 1;
  fetchList();
};

const resetQuery = () => {
  query.keyword = "";
  query.isBlacklisted = undefined;
  query.nameStatus = undefined;
  query.sortField = "";
  query.sortOrder = "";
  pagination.current = 1;
  fetchList();
};

const handleTableChange = (pag, _filters, sorter) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
  query.sortField = activeSorter?.order ? activeSorter.field || activeSorter.columnKey || "" : "";
  query.sortOrder = activeSorter?.order || "";
  fetchList();
};

const rowClassName = (record) => (record.isBlacklisted ? "blacklisted-row" : "");

const handleUrlCopy = () => {
  message.success("详情页链接已复制");
};

const openEdit = (record) => {
  form.id = record.id;
  form.packageName = record.packageName;
  form.appName = record.appName || "";
  form.isBlacklisted = !!record.isBlacklisted;
  modalVisible.value = true;
};

const handleSave = async () => {
  saving.value = true;
  try {
    const json = await postJson(analyticsUrl(`${API_PATH}/update`), {
      id: form.id,
      appName: form.appName.trim(),
      isBlacklisted: form.isBlacklisted,
    });
    if (json.code !== 200) {
      message.error(json.content || "保存失败");
      return;
    }
    message.success("投放应用已保存");
    modalVisible.value = false;
    fetchList();
  } catch {
    message.error("保存失败，请检查网络后重试");
  } finally {
    saving.value = false;
  }
};

onMounted(fetchList);
</script>

<style scoped>
.ad-platform-page {
  padding: 24px;
}

.page-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.page-heading h1 {
  margin: 0 0 4px;
  color: rgba(0, 0, 0, 0.88);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
}

.page-heading p {
  margin: 0;
  color: rgba(0, 0, 0, 0.58);
  line-height: 1.6;
}

.summary-grid {
  margin-bottom: 16px;
}

.summary-card {
  height: 100%;
  border: 1px solid #f0f0f0;
}

.table-card :deep(.ant-card-body) {
  padding: 20px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.keyword-input {
  width: min(360px, 100%);
}

.filter-select {
  width: 140px;
}

.source-tip {
  margin-bottom: 16px;
}

.app-name {
  font-weight: 500;
}

.package-name {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
}

.url-cell {
  display: inline-flex;
  max-width: 100%;
}

.number-cell {
  font-variant-numeric: tabular-nums;
}

.muted-text,
.field-help {
  color: rgba(0, 0, 0, 0.45);
}

.field-help {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
}

.ellipsis-cell {
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
}

.edit-button {
  padding-inline: 0;
}

.blacklist-warning {
  margin-bottom: 16px;
}

:deep(.blacklisted-row > td) {
  background: #fff7f6 !important;
}

:deep(.blacklisted-row:hover > td) {
  background: #fff1f0 !important;
}

@media (max-width: 767px) {
  .ad-platform-page {
    padding: 16px;
  }

  .page-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .page-heading > .ant-btn,
  .toolbar > .ant-btn,
  .filter-select,
  .keyword-input {
    width: 100%;
    min-height: 40px;
  }

  .table-card :deep(.ant-card-body) {
    padding: 16px;
  }
}
</style>
